/**
 * D-122 / D-124: the trainer reads the « common traps » the AI prepared for each mother tongue
 * (« Pour vous qui parlez <langue> »), then approves or withdraws them. Filtered by the learners'
 * language and by the exam shown; a trainer only sees the exams of their languages (D-120, API).
 */

import {
  type LessonPitfallsTeam,
  type PitfallsReviewEnum,
  getAcademyLessonPitfallsListQueryKey,
  useAcademyLessonPitfallsList,
  useAcademyLessonPitfallsPartialUpdate,
} from "@agencyos/api-client";
import { languageLabel, parsePitfalls, pitfallText } from "@agencyos/core";
import { Badge, Button, GlassCard, SelectField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useLanguages } from "../../academy/ielts/MotherTongue";
import { dateTimeText } from "../billing/shared";
import { useAdminExam } from "./examContext";
import { ErrorList, Pager, useAcademyRoles } from "./shared";

const REVIEWS: PitfallsReviewEnum[] = ["to_review", "approved", "rejected"];
const TONES: Record<string, "warning" | "success" | "danger"> = { to_review: "warning", approved: "success", rejected: "danger" };

function PitfallsRow({ row, languageName, onDone }: { row: LessonPitfallsTeam; languageName: string; onDone: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const save = useAcademyLessonPitfallsPartialUpdate();
  const [error, setError] = useState<unknown>(null);
  const data = parsePitfalls({ status: row.status, content: row.content, review: row.review });
  const review = row.review ?? "to_review";

  async function decide(next: PitfallsReviewEnum) {
    setError(null);
    try {
      await save.mutateAsync({ id: row.id, data: { review: next } });
      await onDone();
    } catch (failure) {
      setError(failure);
    }
  }

  return (
    <li className="stack review-request">
      <div className="section-title">
        <div>
          <strong>{row.lesson_code}</strong> <span className="muted">· {languageName}</span>
        </div>
        <Badge tone={TONES[review] ?? "warning"} dot>
          {t(`pitfallsReview.status.${review}`)}
        </Badge>
      </div>
      {data.items.length ? (
        <ul className="stack">
          {data.items.map((item) => {
            const text = pitfallText(item, i18n.language);
            return (
              <li key={item.title}>
                <strong>{item.title}</strong> — {text.main}
                {text.native ? <div className="muted">{text.native}</div> : null}
                {item.example ? <div className="muted">{t("pitfallsReview.example", { example: item.example })}</div> : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted">{t("pitfallsReview.noItems")}</p>
      )}
      {row.reviewed_at ? <p className="muted">{t("pitfallsReview.reviewedAt", { date: dateTimeText(row.reviewed_at, i18n.language) })}</p> : null}
      <ErrorList error={error} />
      {canEdit && data.items.length ? (
        <div className="row">
          <Button size="sm" disabled={save.isPending || review === "approved"} onClick={() => void decide("approved")}>
            {t("pitfallsReview.approve")}
          </Button>
          <Button size="sm" variant="secondary" disabled={save.isPending || review === "rejected"} onClick={() => void decide("rejected")}>
            {t("pitfallsReview.reject")}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

export function PitfallsReviewTab() {
  const { t, i18n } = useTranslation();
  const { exam } = useAdminExam();
  const queryClient = useQueryClient();
  const languages = useLanguages();
  const [language, setLanguage] = useState("");
  const [review, setReview] = useState<PitfallsReviewEnum>("to_review");
  const [page, setPage] = useState(1);
  const list = useAcademyLessonPitfallsList({ exam, review, page, ...(language ? { language } : {}) });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const nameOf = (code: string) => {
    const found = languages.find((l) => l.code === code);
    return found ? languageLabel(found, i18n.language) : code;
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: getAcademyLessonPitfallsListQueryKey() });

  return (
    <GlassCard as="section" className="stack" aria-labelledby="academy-pitfalls-review">
      <div>
        <h2 id="academy-pitfalls-review">{t("pitfallsReview.title")}</h2>
        <p className="muted">{t("pitfallsReview.intro")}</p>
      </div>
      <div className="row">
        <SelectField
          label={t("pitfallsReview.language")}
          value={language}
          onChange={(event) => {
            setLanguage(event.target.value);
            setPage(1);
          }}
          options={[{ value: "", label: t("pitfallsReview.allLanguages") }, ...languages.map((l) => ({ value: l.code, label: languageLabel(l, i18n.language) }))]}
        />
        <SelectField
          label={t("pitfallsReview.filter")}
          value={review}
          onChange={(event) => {
            setReview(REVIEWS.find((r) => r === event.target.value) ?? "to_review");
            setPage(1);
          }}
          options={REVIEWS.map((r) => ({ value: r, label: t(`pitfallsReview.status.${r}`) }))}
        />
      </div>
      {data && data.results.length ? (
        <>
          <ul className="stack review-requests">
            {data.results.map((row) => (
              <PitfallsRow key={row.id} row={row} languageName={nameOf(row.language)} onDone={refresh} />
            ))}
          </ul>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : data ? (
        <p className="muted">{t("pitfallsReview.empty")}</p>
      ) : null}
    </GlassCard>
  );
}
